import i18n from "i18n-js"
import { Translations } from "./translations/en"

/**
 * Builds up valid key paths for translations.
 */
export type TxKeyPath = RecursiveKeyOf<Translations>

// via: https://stackoverflow.com/a/65333050
type RecursiveKeyOf<TObj extends object> = {
  [TKey in keyof TObj & (string | number)]: RecursiveKeyOfHandleValue<TObj[TKey], `${TKey}`>
}[keyof TObj & (string | number)]

type RecursiveKeyOfInner<TObj extends object> = {
  [TKey in keyof TObj & (string | number)]: RecursiveKeyOfHandleValue<
    TObj[TKey],
    `['${TKey}']` | `.${TKey}`
  >
}[keyof TObj & (string | number)]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RecursiveKeyOfHandleValue<TValue, Text extends string> = TValue extends any[]
  ? Text
  : TValue extends object
  ? Text | `${Text}${RecursiveKeyOfInner<TValue>}`
  : Text

type TranslateOptions = i18n.TranslateOptions & { locale?: string }

const getTranslation = (key: TxKeyPath, options?: TranslateOptions) => {
  const translation = i18n.t(key, { ...options, defaultValue: key })

  // Check if we are in production and the translation is missing
  const isProduction = process.env.NODE_ENV !== "development"
  if (isProduction && translation === key) {
    // Save the current locale
    const originalLocale = i18n.locale

    // Temporarily switch to English to try getting the translation
    i18n.locale = "en"
    const englishTranslation = i18n.t(key, { ...options, defaultValue: key })

    // Restore the original locale
    i18n.locale = originalLocale

    // If English translation is also missing, return the key with original locale
    return englishTranslation !== key
      ? englishTranslation
      : `Translation not found ${originalLocale}.${key}`
  }

  // If translation is found or we are not in production, return the translation
  // If translation is missing in non-production, return key with locale
  return translation !== key ? translation : `${i18n.locale}.${key}`
}

/**
 * Translates text.
 *
 * @param {TxKeyPath} key The i18n key.
 * @param {TranslateOptions} options The i18n options.
 * @return {string} The translated text.
 *
 * @example
 * Translations:
 *
 * ```en.ts
 * {
 *  "hello": "Hello, {{name}}!"
 * }
 * ```
 *
 * Usage:
 * ```ts
 * import { translate } from "i18n-js"
 *
 * translate("common.ok", { name: "world" })
 * // => "Hello world!"
 * ```
 */
export function translate(key: TxKeyPath, options?: TranslateOptions): string {
  if (options?.locale && i18n.locale !== options.locale) {
    const currentLocale = i18n.locale
    i18n.locale = options.locale

    const translation = getTranslation(key, options)
    i18n.locale = currentLocale

    return translation
  }

  return getTranslation(key, options)
}

export const _t = translate
